/**
 * Andreas motion — scroll reveal, ambient cursor glow, lightweight.
 * Respects prefers-reduced-motion.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  var REVEAL_SELECTORS = [
    '.andreas-hero__label',
    '.andreas-hero__title',
    '.andreas-hero__text',
    '.andreas-hero__cta',
    '.andreas-tech__heading',
    '.andreas-tech__card',
    '.andreas-product__badge',
    '.andreas-product__title',
    '.andreas-product__subtitle',
    '.andreas-product__media',
    '.andreas-product__section-title',
    '.andreas-product__spec',
    '.andreas-product__highlight',
    '.andreas-product__cta-title',
    '.andreas-product__cta-body',
    '.andreas-product__cta-button',
    '#shopify-section-concept .rich-text__heading',
    '#shopify-section-concept .rich-text__text',
    '#shopify-section-company .rich-text__heading',
    '#shopify-section-company .rich-text__text',
    '#waitlist .newsletter .h4',
    '#waitlist .newsletter__subheading',
    '#waitlist .newsletter-form',
    '.template-page .banner__heading',
    '.template-page .banner__text',
    '.template-page .rich-text__heading',
    '.template-page .rich-text__text',
    '.template-page .contact .title',
    '.template-page .contact__list',
    '.contact__button',
  ].join(',');

  var STAGGER_GROUPS = [
    '.andreas-tech__grid',
    '.andreas-product__specs-grid',
    '.andreas-product__highlights-grid'
  ];

  function prefersReduced() {
    return reduceMotion.matches;
  }

  function markVisible(el) {
    el.classList.add('is-visible');
  }

  function prepareRevealTargets() {
    var nodes = document.querySelectorAll(REVEAL_SELECTORS);
    nodes.forEach(function (el) {
      el.classList.add('andreas-reveal');
    });

    STAGGER_GROUPS.forEach(function (groupSel) {
      document.querySelectorAll(groupSel).forEach(function (group) {
        var children = group.querySelectorAll('.andreas-reveal, .andreas-tech__card, .andreas-product__spec, .andreas-product__highlight');
        children.forEach(function (child, index) {
          child.classList.add('andreas-reveal');
          child.style.setProperty('--andreas-reveal-delay', index * 100 + 'ms');
        });
      });
    });
  }

  function initReveal() {
    if (prefersReduced()) {
      document.querySelectorAll('.andreas-reveal').forEach(markVisible);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.andreas-reveal').forEach(markVisible);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            markVisible(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.12
      }
    );

    document.querySelectorAll('.andreas-reveal').forEach(function (el) {
      observer.observe(el);
    });
  }

  function initCursorGlow() {
    // Stealth portal has its own spotlight — skip here.
    if (
      document.body.classList.contains('template-index-stealth') ||
      document.documentElement.classList.contains('template-index-stealth') ||
      document.querySelector('.stealth-wrapper')
    ) {
      return;
    }

    if (prefersReduced()) return;

    var layer = document.getElementById('andreas-bg-glow');
    if (!layer) {
      layer = document.createElement('div');
      layer.id = 'andreas-bg-glow';
      layer.className = 'andreas-bg-glow';
      layer.setAttribute('aria-hidden', 'true');
      document.body.appendChild(layer);
    }

    var raf = 0;
    var targetX = window.innerWidth * 0.5;
    var targetY = window.innerHeight * 0.35;
    var currentX = targetX;
    var currentY = targetY;
    var active = false;

    function paint() {
      currentX += (targetX - currentX) * 0.1;
      currentY += (targetY - currentY) * 0.1;
      layer.style.background =
        'radial-gradient(650px circle at ' +
        currentX.toFixed(1) +
        'px ' +
        currentY.toFixed(1) +
        'px, rgba(212, 175, 55, 0.14), rgba(180, 130, 20, 0.04) 42%, transparent 72%)';

      if (Math.abs(targetX - currentX) > 0.4 || Math.abs(targetY - currentY) > 0.4) {
        raf = requestAnimationFrame(paint);
      } else {
        raf = 0;
        active = false;
      }
    }

    window.addEventListener(
      'mousemove',
      function (e) {
        targetX = e.clientX;
        targetY = e.clientY;
        if (!active) {
          active = true;
          raf = requestAnimationFrame(paint);
        }
      },
      { passive: true }
    );
  }

  function getWaitlistTarget() {
    return (
      document.getElementById('waitlist') ||
      document.querySelector('[id*="waitlist"] .newsletter') ||
      document.querySelector('.newsletter')
    );
  }

  function scrollToWaitlist() {
    var target = getWaitlistTarget();
    if (!target) return false;

    var header = document.querySelector('.shopify-section-header-sticky, sticky-header, .header-wrapper');
    var offset = header ? header.getBoundingClientRect().height + 12 : 80;
    var top = target.getBoundingClientRect().top + window.pageYOffset - offset;

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: prefersReduced() ? 'auto' : 'smooth'
    });
    return true;
  }

  function initWaitlistScroll() {
    document.addEventListener('click', function (event) {
      var link = event.target.closest('a[data-andreas-scroll="waitlist"], a.andreas-hero__cta, a[href="#waitlist"], a[href="/#waitlist"], a[href*="#waitlist"]');
      if (!link) return;

      if (getWaitlistTarget()) {
        event.preventDefault();
        scrollToWaitlist();
        if (history.replaceState) {
          history.replaceState(null, '', '#waitlist');
        }
      }
    });

    function handleHash() {
      var hash = window.location.hash || '';
      if (hash === '#waitlist' || hash.indexOf('waitlist') !== -1) {
        window.setTimeout(scrollToWaitlist, 120);
      }
    }

    handleHash();
    window.addEventListener('hashchange', handleHash);
  }

  function initAmbientVideos() {
    var videos = document.querySelectorAll('.andreas-hero-bg__video, .andreas-product__media-video');
    if (!videos.length) return;

    videos.forEach(function (video) {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      if (prefersReduced()) {
        video.pause();
        video.removeAttribute('autoplay');
        return;
      }

      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {
          var retry = function () {
            video.play().catch(function () {});
            document.removeEventListener('touchstart', retry);
            document.removeEventListener('click', retry);
          };
          document.addEventListener('touchstart', retry, { once: true, passive: true });
          document.addEventListener('click', retry, { once: true });
        });
      }
    });
  }

  function initProductVideoGlow() {
    if (prefersReduced()) return;

    var shells = document.querySelectorAll('.andreas-product__media-shell');
    shells.forEach(function (shell) {
      var media = shell.querySelector('.andreas-product__media');
      var video = media && media.querySelector('video.andreas-product__media-video');
      if (!video) return;

      var bloom = shell.querySelector('.andreas-product__media-bloom');
      if (!bloom) {
        bloom = video.cloneNode(true);
        bloom.className = 'andreas-product__media-bloom';
        bloom.removeAttribute('aria-label');
        bloom.setAttribute('aria-hidden', 'true');
        bloom.removeAttribute('controls');
        bloom.muted = true;
        bloom.defaultMuted = true;
        bloom.loop = true;
        bloom.playsInline = true;
        bloom.tabIndex = -1;
        shell.insertBefore(bloom, media);
      }

      var sample = document.createElement('canvas');
      sample.width = 32;
      sample.height = 18;
      sample.setAttribute('aria-hidden', 'true');
      sample.style.display = 'none';
      shell.appendChild(sample);
      var ctx = sample.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;

      var syncing = false;
      var lastSample = 0;

      function ensurePlay(el) {
        var p = el.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }

      function syncBloom() {
        if (syncing || !bloom || bloom.readyState < 1) return;
        if (Math.abs((bloom.currentTime || 0) - (video.currentTime || 0)) > 0.12) {
          syncing = true;
          try {
            bloom.currentTime = video.currentTime;
          } catch (e) {}
          syncing = false;
        }
      }

      function sampleGlow(now) {
        if (!ctx || video.readyState < 2) return;
        if (now - lastSample < 80) return;
        lastSample = now;

        try {
          ctx.drawImage(video, 0, 0, sample.width, sample.height);
          var data = ctx.getImageData(0, 0, sample.width, sample.height).data;
          var r = 0;
          var g = 0;
          var b = 0;
          var count = 0;
          var i;

          for (i = 0; i < data.length; i += 16) {
            r += data[i];
            g += data[i + 1];
            b += data[i + 2];
            count += 1;
          }

          if (!count) return;
          r = Math.round(r / count);
          g = Math.round(g / count);
          b = Math.round(b / count);

          // Lift dark frames slightly so the halo stays visible.
          var lift = 38;
          r = Math.min(255, r + lift);
          g = Math.min(255, g + lift);
          b = Math.min(255, b + lift);

          // Bias toward brand gold so launch pages match the stealth luxury palette.
          r = Math.round(r * 0.45 + 212 * 0.55);
          g = Math.round(g * 0.45 + 175 * 0.55);
          b = Math.round(b * 0.45 + 55 * 0.55);

          shell.style.setProperty('--andreas-media-glow', r + ', ' + g + ', ' + b);

          var luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
          bloom.style.opacity = String(0.35 + luminance * 0.35);
        } catch (err) {
          // Cross-origin paint can fail; CSS bloom video still provides the effect.
        }
      }

      function tick(now) {
        syncBloom();
        sampleGlow(now || performance.now());
        requestAnimationFrame(tick);
      }

      video.addEventListener('play', function () {
        ensurePlay(bloom);
      });
      video.addEventListener('seeked', syncBloom);
      video.addEventListener('loadeddata', function () {
        ensurePlay(bloom);
        syncBloom();
      });

      ensurePlay(video);
      ensurePlay(bloom);
      requestAnimationFrame(tick);
    });
  }

  function initSmartLogoLink() {
    var links = document.querySelectorAll('[data-andreas-logo-link]');
    if (!links.length) return;

    links.forEach(function (link) {
      var home = link.getAttribute('data-andreas-home') || '/';
      // Logo always returns to the YOUR DESK, ALIVE homepage.
      link.setAttribute('href', home);
    });
  }

  function boot() {
    prepareRevealTargets();
    initReveal();
    initCursorGlow();
    initWaitlistScroll();
    initAmbientVideos();
    initProductVideoGlow();
    initSmartLogoLink();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', function () {
      if (prefersReduced()) {
        document.querySelectorAll('.andreas-reveal').forEach(markVisible);
      }
    });
  }
})();
